import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Radio,
  Plus,
  Search,
  Play,
  Pause,
  Trash2,
  MoreVertical,
  Clock,
  Activity,
  Zap,
  Users,
  TrendingUp,
  RefreshCw,
  Edit3,
  Sparkles,
  ArrowRight,
  Eye,
  MessageSquare,
  Send,
  X,
  ChevronLeft,
  ChevronRight,
  Download,
  Check,
  Tag,
  Settings,
  Wifi,
  WifiOff,
  ShoppingBag,
} from 'lucide-react';
import toast from 'react-hot-toast';
import Modal from '../../components/ui/Modal';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Badge from '../../components/ui/Badge';
import {
  liveAutomationService,
  LiveSession,
  KeywordRule,
  LiveContact,
  DetectedLive,
} from '../../services/liveAutomationService';
import { socialService } from '../../services/socialService';
import { getWorkflows } from '../../services/workflowService';
import type { SocialAccount } from '../../services/socialService';

// Platform config for icons and colors
const PLATFORM_CONFIG: Record<string, { color: string; bgColor: string; darkBgColor: string; label: string }> = {
  facebook: { color: 'text-blue-600', bgColor: 'bg-blue-100', darkBgColor: 'dark:bg-blue-900/30', label: 'Facebook Live' },
  instagram: { color: 'text-pink-600', bgColor: 'bg-pink-100', darkBgColor: 'dark:bg-pink-900/30', label: 'Instagram Live' },
  youtube: { color: 'text-red-600', bgColor: 'bg-red-100', darkBgColor: 'dark:bg-red-900/30', label: 'YouTube Live' },
  tiktok: { color: 'text-gray-900', bgColor: 'bg-gray-100', darkBgColor: 'dark:bg-gray-800', label: 'TikTok Live' },
};

const ACTION_TYPES = ['private_reply', 'capture_only'] as const;
const MATCH_TYPES = ['exact', 'contains'] as const;

const emptyRule: KeywordRule = {
  keyword: '',
  match_type: 'contains',
  action: 'workflow',
  workflow_id: '',
  workflow_name: '',
};

const WORKFLOW_TEMPLATES = [
  { id: 'tpl_private_reply', nameKey: 'liveAutomation.templates.privateReply', descKey: 'liveAutomation.templates.privateReplyDesc', icon: 'Send' },
  { id: 'tpl_reply_followup', nameKey: 'liveAutomation.templates.replyFollowup', descKey: 'liveAutomation.templates.replyFollowupDesc', icon: 'Clock' },
  { id: 'tpl_capture_remarketing', nameKey: 'liveAutomation.templates.captureRemarketing', descKey: 'liveAutomation.templates.captureRemarketingDesc', icon: 'Users' },
  { id: 'tpl_reply_coupon', nameKey: 'liveAutomation.templates.replyCoupon', descKey: 'liveAutomation.templates.replyCouponDesc', icon: 'Tag' },
];

const LiveAutomationPage: React.FC = () => {
  const { t } = useTranslation();

  // State
  const [sessions, setSessions] = useState<LiveSession[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [platformFilter, setPlatformFilter] = useState<string>('all');
  const [activeMenu, setActiveMenu] = useState<string | null>(null);

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [editingSession, setEditingSession] = useState<LiveSession | null>(null);
  const [activeTab, setActiveTab] = useState(0);
  const [isSaving, setIsSaving] = useState(false);

  // Form state
  const [formPlatform, setFormPlatform] = useState<string>('instagram');
  const [formSocialAccountId, setFormSocialAccountId] = useState<string>('');
  const [formLiveId, setFormLiveId] = useState<string>('');
  const [formTitle, setFormTitle] = useState<string>('');
  const [formKeywordRules, setFormKeywordRules] = useState<KeywordRule[]>([{ ...emptyRule }]);
  const [formAutoDetect, setFormAutoDetect] = useState(true);
  const [formResponseDelay, setFormResponseDelay] = useState(0);
  const [formMaxPerMinute, setFormMaxPerMinute] = useState(30);
  const [formStoreSlug, setFormStoreSlug] = useState('');
  const [marketplaceProducts, setMarketplaceProducts] = useState<any[]>([]);
  const [isLoadingProducts, setIsLoadingProducts] = useState(false);
  const [productSearch, setProductSearch] = useState('');
  const [selectingProductForRule, setSelectingProductForRule] = useState<number | null>(null);


  // Social accounts
  const [socialAccounts, setSocialAccounts] = useState<SocialAccount[]>([]);
  const [workflows, setWorkflows] = useState<any[]>([]);

  // Detail view state
  const [selectedSession, setSelectedSession] = useState<LiveSession | null>(null);
  const [contacts, setContacts] = useState<LiveContact[]>([]);
  const [contactsPage, setContactsPage] = useState(1);
  const [contactsTotal, setContactsTotal] = useState(0);
  const [contactsTotalPages, setContactsTotalPages] = useState(1);
  const [isLoadingContacts, setIsLoadingContacts] = useState(false);

  // Detect lives state
  const [isDetecting, setIsDetecting] = useState(false);
  const [detectedLives, setDetectedLives] = useState<DetectedLive[]>([]);
  const [showDetectModal, setShowDetectModal] = useState(false);

  // Monitoring state
  const [monitoringAction, setMonitoringAction] = useState<string | null>(null);

  // Test keyword state
  const [testKeywordText, setTestKeywordText] = useState('');
  const [testResult, setTestResult] = useState<any>(null);

  // Notify all state
  const [showNotifyModal, setShowNotifyModal] = useState(false);
  const [notifyMessage, setNotifyMessage] = useState('');
  const [isSendingNotify, setIsSendingNotify] = useState(false);

  // Stats
  const [globalStats, setGlobalStats] = useState({
    totalSessions: 0,
    activeNow: 0,
    totalCaptures: 0,
    totalDMs: 0,
  });

  // Load data
  useEffect(() => {
    loadSessions();
    loadSocialAccounts();
    loadWorkflows();
  }, []);

  const loadWorkflows = async () => {
    try {
      const response = await getWorkflows({ limit: 100, status: 'active' });
      const data = response.data || response.workflows || [];
      setWorkflows(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error loading workflows:', error);
    }
  };

  const loadSessions = async () => {
    setIsLoading(true);
    try {
      const response = await liveAutomationService.getSessions();
      const data = response.data || response.sessions || [];
      setSessions(Array.isArray(data) ? data : []);
      calculateGlobalStats(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error loading sessions:', error);
      toast.error(t('liveAutomation.loadError', 'Error loading sessions'));
    } finally {
      setIsLoading(false);
    }
  };

  const loadSocialAccounts = async () => {
    try {
      const response = await socialService.getAccounts();
      const accounts = (response as any).data || (response as any).accounts || [];
      setSocialAccounts(Array.isArray(accounts) ? accounts : []);
    } catch (error) {
      console.error('Error loading social accounts:', error);
    }
  };

  const calculateGlobalStats = (data: LiveSession[]) => {
    setGlobalStats({
      totalSessions: data.length,
      activeNow: data.filter(s => s.status === 'monitoring').length,
      totalCaptures: data.reduce((acc, s) => acc + (s.stats?.total_captures || 0), 0),
      totalDMs: data.reduce((acc, s) => acc + (s.stats?.total_dms_sent || 0), 0),
    });
  };

  const loadContacts = useCallback(async (sessionId: string, page: number = 1) => {
    setIsLoadingContacts(true);
    try {
      const response = await liveAutomationService.getContacts(sessionId, { page, limit: 20 });
      const data = response.data || response.contacts || [];
      setContacts(Array.isArray(data) ? data : []);
      setContactsTotal(response.pagination?.total || 0);
      setContactsTotalPages(response.pagination?.totalPages || 1);
      setContactsPage(page);
    } catch (error) {
      console.error('Error loading contacts:', error);
    } finally {
      setIsLoadingContacts(false);
    }
  }, []);

  // Real-time stats polling for active monitoring sessions
  const pollingRef = useRef<NodeJS.Timeout | null>(null);
  const hasActiveSession = sessions.some(s => s.status === 'monitoring');
  const selectedSessionId = selectedSession?.id;

  useEffect(() => {
    if (hasActiveSession) {
      pollingRef.current = setInterval(async () => {
        try {
          const response = await liveAutomationService.getSessions();
          const data = response.data || response.sessions || [];
          if (Array.isArray(data)) {
            setSessions(data);
            calculateGlobalStats(data);
            // Update selected session if viewing detail
            if (selectedSessionId) {
              const updated = data.find((s: LiveSession) => s.id === selectedSessionId);
              if (updated) setSelectedSession(updated);
            }
          }
        } catch (error) {
          console.error('Polling error:', error);
        }
      }, 5000);
    }

    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
    };
  }, [hasActiveSession, selectedSessionId]);

  // Filtered sessions
  const filteredSessions = sessions.filter(session => {
    const matchesSearch = (session.title || '').toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || session.status === statusFilter;
    const matchesPlatform = platformFilter === 'all' || session.platform === platformFilter;
    return matchesSearch && matchesStatus && matchesPlatform;
  });

  // Handlers
  const resetForm = () => {
    setFormPlatform('instagram');
    setFormSocialAccountId('');
    setFormLiveId('');
    setFormTitle('');
    setFormKeywordRules([{ ...emptyRule }]);
    setFormAutoDetect(true);
    setFormResponseDelay(0);
    setFormMaxPerMinute(30);
    setFormStoreSlug('');
    setActiveTab(0);
    setTestKeywordText('');
    setTestResult(null);
  };

  const handleNewSession = () => {
    setEditingSession(null);
    resetForm();
    setShowModal(true);
  };

  const handleEditSession = (session: LiveSession) => {
    setEditingSession(session);
    setFormPlatform(session.platform);
    setFormSocialAccountId(session.social_account_id || '');
    setFormLiveId(session.platform_live_id || '');
    setFormTitle(session.title || '');
    setFormKeywordRules(session.keyword_rules?.length ? session.keyword_rules : [{ ...emptyRule }]);
    setFormAutoDetect(session.settings?.auto_detect ?? true);
    setFormResponseDelay((session.settings as any)?.response_delay_ms || 0);
    setFormMaxPerMinute((session.settings as any)?.max_responses_per_minute || 30);
    setFormStoreSlug(session.marketplace_store_slug || '');
    setActiveTab(0);
    setTestKeywordText('');
    setTestResult(null);
    setShowModal(true);
  };

  const handleSaveSession = async () => {
    if (!formTitle.trim()) {
      toast.error(t('liveAutomation.config.sessionTitleRequired', 'Session title is required'));
      setActiveTab(0);
      return;
    }

    setIsSaving(true);
    try {
      const payload: Partial<LiveSession> = {
        platform: formPlatform as LiveSession['platform'],
        social_account_id: formSocialAccountId || undefined,
        platform_live_id: formLiveId || undefined,
        title: formTitle,
        keyword_rules: formKeywordRules.filter(r => r.keyword.trim()),
        marketplace_store_slug: formStoreSlug || undefined,
        settings: {
          auto_detect: formAutoDetect,
          response_delay_ms: formResponseDelay,
          max_responses_per_minute: formMaxPerMinute,
        },
      };

      if (editingSession) {
        await liveAutomationService.updateSession(editingSession.id, payload);
        toast.success(t('liveAutomation.sessionUpdated'));
      } else {
        await liveAutomationService.createSession(payload);
        toast.success(t('liveAutomation.sessionCreated'));
      }
      setShowModal(false);
      loadSessions();
    } catch (error: any) {
      toast.error(error?.response?.data?.message || t('common.error'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteSession = async (id: string) => {
    if (!confirm(t('liveAutomation.deleteConfirm'))) return;
    try {
      await liveAutomationService.deleteSession(id);
      toast.success(t('liveAutomation.sessionDeleted'));
      setSessions(sessions.filter(s => s.id !== id));
      if (selectedSession?.id === id) {
        setSelectedSession(null);
      }
    } catch (error) {
      toast.error(t('common.error'));
    }
  };

  const handleStartMonitoring = async (session: LiveSession) => {
    // Pre-validate before calling API
    if (!session.social_account_id) {
      toast.error(t('liveAutomation.monitoring.needAccount', 'Connect a social account first. Edit the session to link one.'));
      return;
    }
    if (!session.keyword_rules || session.keyword_rules.length === 0) {
      toast.error(t('liveAutomation.monitoring.needKeywords', 'Add at least one keyword rule before starting.'));
      return;
    }
    setMonitoringAction(session.id);
    try {
      const response = await liveAutomationService.startMonitoring(session.id);
      toast.success(t('liveAutomation.monitoring.started'));
      const updatedSession = response.data || { ...session, status: 'monitoring' };
      // Update local state immediately
      setSessions(prev => prev.map(s => s.id === session.id ? { ...s, status: 'monitoring', started_at: s.started_at || new Date().toISOString() } : s));
      if (selectedSession?.id === session.id) {
        setSelectedSession({ ...selectedSession, status: 'monitoring', started_at: selectedSession.started_at || new Date().toISOString() });
      }
      calculateGlobalStats(sessions.map(s => s.id === session.id ? { ...s, status: 'monitoring' } : s));
    } catch (error: any) {
      const msg = error?.response?.data?.message || t('common.error');
      toast.error(msg);
    } finally {
      setMonitoringAction(null);
    }
  };

  const handleStopMonitoring = async (session: LiveSession) => {
    setMonitoringAction(session.id);
    try {
      await liveAutomationService.stopMonitoring(session.id);
      toast.success(t('liveAutomation.monitoring.stopped'));
      // Update local state immediately
      setSessions(prev => prev.map(s => s.id === session.id ? { ...s, status: 'paused' } : s));
      if (selectedSession?.id === session.id) {
        setSelectedSession({ ...selectedSession, status: 'paused' });
      }
      calculateGlobalStats(sessions.map(s => s.id === session.id ? { ...s, status: 'paused' } : s));
    } catch (error: any) {
      toast.error(error?.response?.data?.message || t('common.error'));
    } finally {
      setMonitoringAction(null);
    }
  };

  const handleDetectLives = async () => {
    setIsDetecting(true);
    setShowDetectModal(true);
    try {
      const response = await liveAutomationService.detectLives();
      const lives = response.data || response.lives || [];
      setDetectedLives(Array.isArray(lives) ? lives : []);
      if (!lives || lives.length === 0) {
        toast(t('liveAutomation.monitoring.noLivesDetected'), { icon: '📡' });
      } else {
        toast.success(t('liveAutomation.monitoring.liveDetected'));
      }
    } catch (error) {
      toast.error(t('common.error'));
      setDetectedLives([]);
    } finally {
      setIsDetecting(false);
    }
  };

  const handleViewContacts = (session: LiveSession) => {
    setSelectedSession(session);
    loadContacts(session.id, 1);
  };


  const handleTestKeyword = async () => {
    if (!editingSession || !testKeywordText.trim()) return;
    try {
      const response = await liveAutomationService.testKeyword(editingSession.id, testKeywordText);
      setTestResult(response.data || response);
    } catch (error) {
      toast.error(t('common.error'));
    }
  };

  const handleNotifyAll = async () => {
    if (!selectedSession || !notifyMessage.trim()) return;
    setIsSendingNotify(true);
    try {
      const response = await liveAutomationService.notifyAll(selectedSession.id, notifyMessage);
      toast.success(t('liveAutomation.contacts.notifySent', `Sent: ${response.sent || 0}, Failed: ${response.failed || 0}`));
      setShowNotifyModal(false);
      setNotifyMessage('');
    } catch (error: any) {
      toast.error(error?.response?.data?.message || t('common.error'));
    } finally {
      setIsSendingNotify(false);
    }
  };

  // Keyword rules management
  const loadMarketplaceProducts = async (search?: string) => {
    if (!formStoreSlug) return;
    setIsLoadingProducts(true);
    try {
      const response = await liveAutomationService.getMarketplaceProducts(formStoreSlug, search);
      const products = response.data || [];
      setMarketplaceProducts(Array.isArray(products) ? products : []);
      if (Array.isArray(products) && products.length === 0) {
        toast(t('liveAutomation.marketplace.noProducts', 'No products found'), { icon: '📦' });
      }
    } catch (error: any) {
      console.error('Error loading marketplace products:', error);
      const msg = error?.response?.data?.message || t('liveAutomation.marketplace.loadError', 'Could not load products. Check if the store slug is correct.');
      toast.error(msg);
      setMarketplaceProducts([]);
    } finally {
      setIsLoadingProducts(false);
    }
  };

  const selectProductForRule = (ruleIndex: number, product: any) => {
    const updated = [...formKeywordRules];
    (updated[ruleIndex] as any).product_name = product.name;
    (updated[ruleIndex] as any).product_url = `https://${formStoreSlug}.devskin.com/produto/${product.slug}`;
    (updated[ruleIndex] as any).product_image = product.images?.[0]?.url || '';
    setFormKeywordRules(updated);
    setSelectingProductForRule(null);
  };

  const addRule = () => {
    setFormKeywordRules([...formKeywordRules, { ...emptyRule }]);
  };

  const removeRule = (index: number) => {
    if (formKeywordRules.length <= 1) return;
    setFormKeywordRules(formKeywordRules.filter((_, i) => i !== index));
  };

  const updateRule = (index: number, field: keyof KeywordRule, value: string) => {
    const updated = [...formKeywordRules];
    (updated[index] as any)[field] = value;
    setFormKeywordRules(updated);
  };

  // Helpers
  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return new Intl.DateTimeFormat(undefined, {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  };

  const getStatusConfig = (status: string) => {
    const configs: Record<string, { color: string; icon: React.ReactNode; label: string; pulse?: boolean }> = {
      configuring: {
        color: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
        icon: <Settings className="w-3.5 h-3.5" />,
        label: t('liveAutomation.status.configuring'),
      },
      monitoring: {
        color: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
        icon: <Wifi className="w-3.5 h-3.5" />,
        label: t('liveAutomation.status.monitoring'),
        pulse: true,
      },
      ended: {
        color: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400',
        icon: <WifiOff className="w-3.5 h-3.5" />,
        label: t('liveAutomation.status.ended'),
      },
      paused: {
        color: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400',
        icon: <Pause className="w-3.5 h-3.5" />,
        label: t('liveAutomation.status.paused'),
      },
    };
    return configs[status] || configs.configuring;
  };

  const getActionBadge = (action: string) => {
    const configs: Record<string, { variant: 'primary' | 'success' | 'warning' | 'danger' | 'gray'; label: string }> = {
      workflow_triggered: { variant: 'success', label: t('liveAutomation.contacts.workflowTriggered', 'Workflow Triggered') },
      captured_only: { variant: 'gray', label: t('liveAutomation.contacts.capturedOnly') },
      failed: { variant: 'danger', label: t('liveAutomation.contacts.failed') },
    };
    const config = configs[action] || configs.captured_only;
    return <Badge variant={config.variant} size="sm">{config.label}</Badge>;
  };

  const filteredAccounts = socialAccounts.filter(a =>
    a.platform === formPlatform || a.platform === `${formPlatform}_reels`
  );

  // Auto-select account when there's only one for the platform
  useEffect(() => {
    if (filteredAccounts.length === 1 && !formSocialAccountId) {
      setFormSocialAccountId(filteredAccounts[0].id);
    }
  }, [filteredAccounts, formSocialAccountId]);

  // Tab definitions
  const tabs = [
    { label: t('liveAutomation.tabs.basicConfig'), key: 'basic' },
    { label: t('liveAutomation.tabs.keywordRules'), key: 'keywords' },
    { label: t('liveAutomation.tabs.settings'), key: 'settings' },
    { label: t('liveAutomation.tabs.marketplace'), key: 'marketplace' },
  ];

  // Render detail view
  if (selectedSession) {
    return (
      <div className="space-y-3 sm:space-y-6">
        {/* Back button & header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-3">
            <button
              onClick={() => setSelectedSession(null)}
              className="p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
            >
              <ChevronLeft className="w-5 h-5 text-gray-600 dark:text-gray-400" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-3">
                {selectedSession.title || t('liveAutomation.title')}
                {(() => {
                  const sc = getStatusConfig(selectedSession.status);
                  return (
                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${sc.color}`}>
                      {sc.pulse && <span className="relative flex h-2 w-2"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span><span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span></span>}
                      {sc.icon}
                      {sc.label}
                    </span>
                  );
                })()}
              </h1>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                {t(`liveAutomation.platforms.${selectedSession.platform}`)} &middot; {t('liveAutomation.keywords.title')}: {selectedSession.keyword_rules?.length || 0}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {selectedSession.status === 'monitoring' ? (
              <Button
                variant="secondary"
                leftIcon={<Pause className="w-4 h-4" />}
                onClick={() => handleStopMonitoring(selectedSession)}
                isLoading={monitoringAction === selectedSession.id}
              >
                {t('liveAutomation.monitoring.stop')}
              </Button>
            ) : selectedSession.status !== 'ended' ? (
              <Button
                leftIcon={<Play className="w-4 h-4" />}
                onClick={() => handleStartMonitoring(selectedSession)}
                isLoading={monitoringAction === selectedSession.id}
              >
                {t('liveAutomation.monitoring.start')}
              </Button>
            ) : null}
            <Button
              variant="secondary"
              leftIcon={<Download className="w-4 h-4" />}
              onClick={async () => {
                try {
                  toast(t('liveAutomation.contacts.exporting', 'Exporting...'), { icon: '...' });
                  const response = await liveAutomationService.getAllContacts(selectedSession.id);
                  const allContacts: LiveContact[] = response.data || [];
                  if (allContacts.length === 0) {
                    toast.error(t('liveAutomation.contacts.noContacts'));
                    return;
                  }
                  const headers = ['Username', 'Name', 'Comment', 'Keyword', 'Action', 'Product', 'Coupon', 'Converted', 'Date'];
                  const rows = allContacts.map(c => [
                    c.platform_username || '',
                    c.display_name || '',
                    `"${(c.comment_text || '').replace(/"/g, '""')}"`,
                    c.matched_keyword || '',
                    c.action_taken || '',
                    c.product_url || '',
                    c.coupon_code || '',
                    c.converted ? 'Yes' : 'No',
                    c.captured_at || ''
                  ]);
                  const csv = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
                  const blob = new Blob([csv], { type: 'text/csv' });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = `live-contacts-${selectedSession?.title || 'export'}-${new Date().toISOString().slice(0, 10)}.csv`;
                  a.click();
                  URL.revokeObjectURL(url);
                  toast.success(t('liveAutomation.contacts.exported', `${allContacts.length} contacts exported`));
                } catch (error) {
                  toast.error(t('common.error'));
                }
              }}
            >
              {t('liveAutomation.contacts.exportContacts')}
            </Button>
            <Button
              variant="secondary"
              leftIcon={<Send className="w-4 h-4" />}
              onClick={() => setShowNotifyModal(true)}
            >
              {t('liveAutomation.contacts.notifyAll', 'Notify All')}
            </Button>
          </div>
        </div>

        {/* Session stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-4">
          <div className="card p-4 bg-gradient-to-br from-blue-50 to-cyan-50 dark:from-blue-900/20 dark:to-cyan-900/20 border-blue-200 dark:border-blue-800">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-xl bg-blue-100 dark:bg-blue-900/50">
                <MessageSquare className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <p className="text-xs text-blue-600 dark:text-blue-400 font-medium">{t('liveAutomation.stats.totalComments')}</p>
                <p className="text-2xl font-bold text-blue-700 dark:text-blue-300">{selectedSession.stats?.total_comments || 0}</p>
              </div>
            </div>
          </div>
          <div className="card p-4 bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 border-purple-200 dark:border-purple-800">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-xl bg-purple-100 dark:bg-purple-900/50">
                <Users className="w-5 h-5 text-purple-600 dark:text-purple-400" />
              </div>
              <div>
                <p className="text-xs text-purple-600 dark:text-purple-400 font-medium">{t('liveAutomation.stats.totalCaptures')}</p>
                <p className="text-2xl font-bold text-purple-700 dark:text-purple-300">{selectedSession.stats?.total_captures || 0}</p>
              </div>
            </div>
          </div>
          <div className="card p-4 bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 border-green-200 dark:border-green-800">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-xl bg-green-100 dark:bg-green-900/50">
                <Send className="w-5 h-5 text-green-600 dark:text-green-400" />
              </div>
              <div>
                <p className="text-xs text-green-600 dark:text-green-400 font-medium">{t('liveAutomation.stats.totalDMs')}</p>
                <p className="text-2xl font-bold text-green-700 dark:text-green-300">{selectedSession.stats?.total_dms_sent || 0}</p>
              </div>
            </div>
          </div>
          <div className="card p-4 bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 border-amber-200 dark:border-amber-800">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-xl bg-amber-100 dark:bg-amber-900/50">
                <TrendingUp className="w-5 h-5 text-amber-600 dark:text-amber-400" />
              </div>
              <div>
                <p className="text-xs text-amber-600 dark:text-amber-400 font-medium">{t('liveAutomation.stats.conversionRate')}</p>
                <p className="text-2xl font-bold text-amber-700 dark:text-amber-300">
                  {selectedSession.stats?.total_captures
                    ? `${Math.round((selectedSession.stats.total_dms_sent / selectedSession.stats.total_captures) * 100)}%`
                    : '0%'}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Contacts table */}
        <div className="card overflow-hidden">
          <div className="px-4 sm:px-6 py-2 sm:py-4 border-b border-gray-100 dark:border-gray-800">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              {t('liveAutomation.contacts.title')} ({contactsTotal})
            </h3>
          </div>

          {isLoadingContacts ? (
            <div className="flex items-center justify-center py-20">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-500" />
            </div>
          ) : contacts.length === 0 ? (
            <div className="p-12 text-center">
              <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-gray-100 to-gray-50 dark:from-gray-800 dark:to-gray-900 flex items-center justify-center">
                <Users className="w-8 h-8 text-gray-400" />
              </div>
              <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">
                {t('liveAutomation.contacts.noContacts')}
              </h4>
              <p className="text-sm text-gray-500 dark:text-gray-400 max-w-md mx-auto">
                {t('liveAutomation.contacts.noContactsDesc')}
              </p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto scrollbar-hide">
                <table className="w-full">
                  <thead className="bg-gray-50 dark:bg-gray-800/50">
                    <tr>
                      <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        {t('liveAutomation.contacts.username')}
                      </th>
                      <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider hidden md:table-cell">
                        {t('liveAutomation.contacts.comment')}
                      </th>
                      <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        {t('liveAutomation.contacts.matchedKeyword')}
                      </th>
                      <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider hidden sm:table-cell">
                        {t('liveAutomation.contacts.actionTaken')}
                      </th>
                      <th className="px-3 sm:px-6 py-2 sm:py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider hidden lg:table-cell">
                        {t('liveAutomation.contacts.converted')}
                      </th>
                      <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider hidden lg:table-cell">
                        {t('liveAutomation.contacts.capturedAt')}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                    {contacts.map((contact) => (
                      <tr key={contact.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                        <td className="px-3 sm:px-6 py-2 sm:py-4">
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary-500 to-purple-600 flex items-center justify-center">
                              <span className="text-xs font-bold text-white">
                                {(contact.display_name || contact.platform_username)?.[0]?.toUpperCase() || '?'}
                              </span>
                            </div>
                            <div>
                              <p className="text-sm font-medium text-gray-900 dark:text-white">{contact.display_name || contact.platform_username}</p>
                              <p className="text-xs text-gray-500">@{contact.platform_username}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-3 sm:px-6 py-2 sm:py-4 text-sm text-gray-700 dark:text-gray-300 max-w-[150px] sm:max-w-xs truncate hidden md:table-cell">
                          {contact.comment_text}
                        </td>
                        <td className="px-3 sm:px-6 py-2 sm:py-4">
                          <Badge variant="primary" size="sm">{contact.matched_keyword}</Badge>
                        </td>
                        <td className="px-3 sm:px-6 py-2 sm:py-4 hidden sm:table-cell">{getActionBadge(contact.action_taken)}</td>
                        <td className="px-3 sm:px-6 py-2 sm:py-4 text-center hidden lg:table-cell">
                          {contact.converted ? (
                            <Check className="w-5 h-5 text-green-500 mx-auto" />
                          ) : (
                            <X className="w-5 h-5 text-gray-300 dark:text-gray-600 mx-auto" />
                          )}
                        </td>
                        <td className="px-3 sm:px-6 py-2 sm:py-4 text-sm text-gray-500 hidden lg:table-cell">{formatDate(contact.captured_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {contactsTotalPages > 1 && (
                <div className="px-4 sm:px-6 py-2 sm:py-4 border-t border-gray-100 dark:border-gray-800 flex items-center justify-between">
                  <p className="text-sm text-gray-500">
                    {t('common.page', 'Page')} {contactsPage} / {contactsTotalPages}
                  </p>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="secondary"
                      size="sm"
                      disabled={contactsPage <= 1}
                      onClick={() => loadContacts(selectedSession.id, contactsPage - 1)}
                      leftIcon={<ChevronLeft className="w-4 h-4" />}
                    >
                      {t('common.previous')}
                    </Button>
                    <Button
                      variant="secondary"
                      size="sm"
                      disabled={contactsPage >= contactsTotalPages}
                      onClick={() => loadContacts(selectedSession.id, contactsPage + 1)}
                      rightIcon={<ChevronRight className="w-4 h-4" />}
                    >
                      {t('common.next')}
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Notify All Modal */}
        <Modal
          isOpen={showNotifyModal}
          onClose={() => setShowNotifyModal(false)}
          title={t('liveAutomation.contacts.notifyAll', 'Notify All Contacts')}
          size="md"
        >
          <div className="space-y-4">
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {t('liveAutomation.contacts.notifyAllDesc', 'Send a DM to all captured contacts from this session. Supports {{name}}, {{username}}, {{keyword}}, {{product_name}}, {{product_url}}, {{coupon_code}} placeholders.')}
            </p>
            <textarea
              value={notifyMessage}
              onChange={(e) => setNotifyMessage(e.target.value)}
              placeholder={t('liveAutomation.contacts.notifyPlaceholder', 'Hi {{name}}, thanks for your interest! Here is your link: {{product_url}}')}
              className="w-full p-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white min-h-[120px] focus:ring-2 focus:ring-red-500 focus:border-transparent"
              rows={4}
            />
            <div className="flex items-center justify-end gap-3">
              <Button variant="ghost" onClick={() => setShowNotifyModal(false)}>
                {t('common.cancel')}
              </Button>
              <Button
                onClick={handleNotifyAll}
                isLoading={isSendingNotify}
                disabled={!notifyMessage.trim()}
                leftIcon={<Send className="w-4 h-4" />}
                className="bg-gradient-to-r from-red-500 to-pink-500 hover:from-red-600 hover:to-pink-600"
              >
                {t('liveAutomation.contacts.sendToAll', 'Send to All')}
              </Button>
            </div>
          </div>
        </Modal>

      </div>
    );
  }

  // Main list view
  return (
    <div className="space-y-3 sm:space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3 sm:gap-4">
        <div className="flex items-start gap-3 min-w-0">
          <div className="hidden sm:flex shrink-0 p-2.5 rounded-xl bg-gradient-to-br from-red-500 to-pink-500 shadow-lg shadow-red-500/25">
            <Radio className="w-6 h-6 text-white" />
          </div>
          <div className="min-w-0">
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white leading-tight">
              {t('liveAutomation.title')}
            </h1>
            <p className="text-sm sm:text-base text-gray-500 dark:text-gray-400 mt-0.5">
              {t('liveAutomation.subtitle')}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 sm:gap-3">
          <Button
            variant="secondary"
            leftIcon={<Wifi className="w-4 h-4" />}
            onClick={handleDetectLives}
            isLoading={isDetecting}
            className="flex-1 sm:flex-none h-10 whitespace-nowrap"
          >
            <span className="sm:hidden">{t('liveAutomation.detectLivesShort', 'Achar lives')}</span>
            <span className="hidden sm:inline">{t('liveAutomation.detectLives')}</span>
          </Button>
          <Button
            leftIcon={<Plus className="w-4 h-4" />}
            onClick={handleNewSession}
            className="flex-1 sm:flex-none h-10 whitespace-nowrap bg-gradient-to-r from-red-500 to-pink-500 hover:from-red-600 hover:to-pink-600 shadow-lg shadow-red-500/25"
          >
            {t('liveAutomation.newSession')}
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-4">
        <div className="card p-4 bg-gradient-to-br from-red-50 to-pink-50 dark:from-red-900/20 dark:to-pink-900/20 border-red-200 dark:border-red-800">
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-xl bg-red-100 dark:bg-red-900/50">
              <Radio className="w-5 h-5 text-red-600 dark:text-red-400" />
            </div>
            <div>
              <p className="text-xs text-red-600 dark:text-red-400 font-medium">{t('liveAutomation.stats.totalSessions')}</p>
              <p className="text-2xl font-bold text-red-700 dark:text-red-300">{globalStats.totalSessions}</p>
            </div>
          </div>
        </div>

        <div className="card p-4 bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 border-green-200 dark:border-green-800">
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-xl bg-green-100 dark:bg-green-900/50">
              <Activity className="w-5 h-5 text-green-600 dark:text-green-400" />
            </div>
            <div>
              <p className="text-xs text-green-600 dark:text-green-400 font-medium">{t('liveAutomation.stats.activeNow')}</p>
              <p className="text-2xl font-bold text-green-700 dark:text-green-300">{globalStats.activeNow}</p>
            </div>
          </div>
        </div>

        <div className="card p-4 bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 border-purple-200 dark:border-purple-800">
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-xl bg-purple-100 dark:bg-purple-900/50">
              <Users className="w-5 h-5 text-purple-600 dark:text-purple-400" />
            </div>
            <div>
              <p className="text-xs text-purple-600 dark:text-purple-400 font-medium">{t('liveAutomation.stats.totalCaptures')}</p>
              <p className="text-2xl font-bold text-purple-700 dark:text-purple-300">{globalStats.totalCaptures}</p>
            </div>
          </div>
        </div>

        <div className="card p-4 bg-gradient-to-br from-blue-50 to-cyan-50 dark:from-blue-900/20 dark:to-cyan-900/20 border-blue-200 dark:border-blue-800">
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-xl bg-blue-100 dark:bg-blue-900/50">
              <Send className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <p className="text-xs text-blue-600 dark:text-blue-400 font-medium">{t('liveAutomation.stats.totalDMs')}</p>
              <p className="text-2xl font-bold text-blue-700 dark:text-blue-300">{globalStats.totalDMs}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="card p-4">
        <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
          <div className="flex flex-wrap items-center gap-2 sm:gap-3 w-full sm:w-auto">
            <div className="relative w-full sm:w-auto">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder={t('common.search') + '...'}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 pr-4 py-2 w-full sm:w-64 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-red-500 focus:border-transparent"
              />
            </div>

            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="flex-1 sm:flex-none min-w-0 px-3 sm:px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-red-500"
            >
              <option value="all">{t('liveAutomation.filters.allStatus', 'Todas as situações')}</option>
              <option value="configuring">{t('liveAutomation.status.configuring')}</option>
              <option value="monitoring">{t('liveAutomation.status.monitoring')}</option>
              <option value="paused">{t('liveAutomation.status.paused')}</option>
              <option value="ended">{t('liveAutomation.status.ended')}</option>
            </select>

            <select
              value={platformFilter}
              onChange={(e) => setPlatformFilter(e.target.value)}
              className="flex-1 sm:flex-none min-w-0 px-3 sm:px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-red-500"
            >
              <option value="all">{t('liveAutomation.filters.allPlatforms', 'Todas as redes')}</option>
              <option value="instagram">{t('liveAutomation.platforms.instagram')}</option>
              <option value="facebook">{t('liveAutomation.platforms.facebook')}</option>
              <option value="youtube">{t('liveAutomation.platforms.youtube')}</option>
            </select>
          </div>

          <button
            onClick={loadSessions}
            className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-500"
            title={t('common.refresh', 'Refresh')}
          >
            <RefreshCw className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Session List */}
      {isLoading ? (
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-red-500" />
        </div>
      ) : filteredSessions.length === 0 ? (
        <div className="card p-6 sm:p-12 text-center">
          <div className="w-16 h-16 sm:w-20 sm:h-20 mx-auto mb-4 sm:mb-6 rounded-2xl bg-gradient-to-br from-red-100 to-pink-100 dark:from-red-900/30 dark:to-pink-900/30 flex items-center justify-center">
            <Radio className="w-10 h-10 text-red-500" />
          </div>
          <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
            {searchQuery || statusFilter !== 'all' || platformFilter !== 'all'
              ? t('liveAutomation.noResults', 'Nenhuma live encontrada com esses filtros')
              : t('liveAutomation.noSessions')}
          </h3>
          <p className="text-gray-500 dark:text-gray-400 mb-6 max-w-md mx-auto">
            {searchQuery || statusFilter !== 'all' || platformFilter !== 'all'
              ? ''
              : t('liveAutomation.noSessionsDesc')}
          </p>
          {!searchQuery && statusFilter === 'all' && platformFilter === 'all' && (
            <Button
              onClick={handleNewSession}
              leftIcon={<Sparkles className="w-5 h-5" />}
              className="bg-gradient-to-r from-red-500 to-pink-500"
            >
              {t('liveAutomation.createSession')}
            </Button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredSessions.map((session) => {
            const statusCfg = getStatusConfig(session.status);
            const pCfg = PLATFORM_CONFIG[session.platform] || PLATFORM_CONFIG.instagram;
            return (
              <div
                key={session.id}
                className="card group hover:shadow-lg hover:shadow-red-500/10 transition-all duration-300 cursor-pointer border-2 border-transparent hover:border-red-200 dark:hover:border-red-800"
                onClick={() => handleViewContacts(session)}
              >
                <div className="p-5">
                  {/* Header */}
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className={`p-2.5 rounded-xl ${pCfg.bgColor} ${pCfg.darkBgColor}`}>
                        <Radio className={`w-5 h-5 ${pCfg.color}`} />
                      </div>
                      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${statusCfg.color}`}>
                        {statusCfg.pulse && (
                          <span className="relative flex h-2 w-2">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                            <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
                          </span>
                        )}
                        {statusCfg.icon}
                        {statusCfg.label}
                      </span>
                    </div>

                    <div className="relative">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setActiveMenu(activeMenu === session.id ? null : session.id);
                        }}
                        className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <MoreVertical className="w-4 h-4 text-gray-500" />
                      </button>

                      {activeMenu === session.id && (
                        <div
                          className="absolute right-0 top-8 w-full sm:w-48 bg-white dark:bg-gray-800 rounded-xl shadow-xl border border-gray-200 dark:border-gray-700 py-2 z-50"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <button
                            onClick={() => { handleEditSession(session); setActiveMenu(null); }}
                            className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-2 text-gray-700 dark:text-gray-300"
                          >
                            <Edit3 className="w-4 h-4" />
                            {t('common.edit')}
                          </button>
                          <button
                            onClick={() => { handleViewContacts(session); setActiveMenu(null); }}
                            className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-2 text-gray-700 dark:text-gray-300"
                          >
                            <Eye className="w-4 h-4" />
                            {t('liveAutomation.contacts.title')}
                          </button>
                          {session.status === 'monitoring' ? (
                            <button
                              onClick={() => { handleStopMonitoring(session); setActiveMenu(null); }}
                              className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-2 text-gray-700 dark:text-gray-300"
                            >
                              <Pause className="w-4 h-4" />
                              {t('liveAutomation.monitoring.stop')}
                            </button>
                          ) : session.status !== 'ended' ? (
                            <button
                              onClick={() => { handleStartMonitoring(session); setActiveMenu(null); }}
                              className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-2 text-gray-700 dark:text-gray-300"
                            >
                              <Play className="w-4 h-4" />
                              {t('liveAutomation.monitoring.start')}
                            </button>
                          ) : null}
                          <hr className="my-2 border-gray-200 dark:border-gray-700" />
                          <button
                            onClick={() => { handleDeleteSession(session.id); setActiveMenu(null); }}
                            className="w-full px-4 py-2 text-left text-sm hover:bg-red-50 dark:hover:bg-red-900/20 text-red-600 flex items-center gap-2"
                          >
                            <Trash2 className="w-4 h-4" />
                            {t('liveAutomation.deleteSession')}
                          </button>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Content */}
                  <h3 className="font-semibold text-gray-900 dark:text-white mb-1 group-hover:text-red-600 dark:group-hover:text-red-400 transition-colors">
                    {session.title || t('liveAutomation.title')}
                  </h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                    {t(`liveAutomation.platforms.${session.platform}`)}
                  </p>

                  {/* Keywords indicator */}
                  <div className="flex items-center gap-2 mb-4 p-2 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
                    <Tag className="w-4 h-4 text-red-500" />
                    <span className="text-xs text-gray-600 dark:text-gray-400">
                      {t('liveAutomation.keywords.title')}: <span className="font-medium">{session.keyword_rules?.length || 0}</span>
                    </span>
                  </div>

                  {/* Stats */}
                  <div className="grid grid-cols-3 gap-2 pt-4 border-t border-gray-100 dark:border-gray-800">
                    <div className="text-center">
                      <p className="text-lg font-bold text-gray-900 dark:text-white">{session.stats?.total_comments || 0}</p>
                      <p className="text-xs text-gray-500">{t('liveAutomation.stats.totalComments')}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-lg font-bold text-purple-600">{session.stats?.total_captures || 0}</p>
                      <p className="text-xs text-gray-500">{t('liveAutomation.stats.totalCaptures')}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-lg font-bold text-green-600">{session.stats?.total_dms_sent || 0}</p>
                      <p className="text-xs text-gray-500">{t('liveAutomation.stats.totalDMs')}</p>
                    </div>
                  </div>

                  {/* Dates */}
                  <div className="flex items-center justify-between mt-4 pt-3 border-t border-gray-100 dark:border-gray-800 text-xs text-gray-500">
                    <span className="flex items-center gap-1">
                      <Clock className="w-3.5 h-3.5" />
                      {session.started_at ? formatDate(session.started_at) : formatDate(session.created_at)}
                    </span>
                    <ArrowRight className="w-4 h-4 text-red-500 opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Create/Edit Modal - Wizard */}
      <Modal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title={editingSession ? t('liveAutomation.editSession') : t('liveAutomation.createSession')}
        size="2xl"
        maxHeight
      >
        <div className="space-y-3 sm:space-y-6">
          {/* Progress indicator */}
          <div className="flex items-center gap-2 -mx-4 sm:-mx-6 px-4 sm:px-6 pb-4 border-b border-gray-200 dark:border-gray-700">
            {[
              { label: t('liveAutomation.config.platform'), icon: <Radio className="w-4 h-4" /> },
              { label: t('liveAutomation.marketplace.title'), icon: <ShoppingBag className="w-4 h-4" /> },
              { label: t('liveAutomation.keywords.title'), icon: <Tag className="w-4 h-4" /> },
              { label: t('liveAutomation.tabs.settings'), icon: <Settings className="w-4 h-4" /> },
            ].map((step, index) => (
              <React.Fragment key={index}>
                {index > 0 && <div className={`flex-1 h-0.5 ${index <= activeTab ? 'bg-red-500' : 'bg-gray-200 dark:bg-gray-700'}`} />}
                <button
                  onClick={() => setActiveTab(index)}
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium transition-all whitespace-nowrap ${
                    index === activeTab
                      ? 'bg-red-500 text-white shadow-lg shadow-red-500/25'
                      : index < activeTab
                      ? 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400'
                      : 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400'
                  }`}
                >
                  {index < activeTab ? <Check className="w-3.5 h-3.5" /> : step.icon}
                  <span className="hidden sm:inline">{step.label}</span>
                  <span className="sm:hidden">{index + 1}</span>
                </button>
              </React.Fragment>
            ))}
          </div>

          {/* Step 1: Platform + Account */}
          {activeTab === 0 && (
            <div className="space-y-4">
              <div>
                <label className="label">{t('liveAutomation.config.platform')}</label>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-3 mt-1">
                  {(['instagram', 'facebook', 'youtube'] as const).map((p) => {
                    const cfg = PLATFORM_CONFIG[p];
                    return (
                      <button
                        key={p}
                        type="button"
                        onClick={() => { setFormPlatform(p); setFormSocialAccountId(''); }}
                        className={`p-3 rounded-xl border-2 transition-all text-center ${
                          formPlatform === p
                            ? 'border-red-500 bg-red-50 dark:bg-red-900/20'
                            : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                        }`}
                      >
                        <div className={`p-2 rounded-lg ${cfg.bgColor} ${cfg.darkBgColor} inline-flex mx-auto mb-2`}>
                          <Radio className={`w-5 h-5 ${cfg.color}`} />
                        </div>
                        <p className="text-sm font-medium text-gray-900 dark:text-white">{t(`liveAutomation.platforms.${p}`)}</p>
                      </button>
                    );
                  })}
                </div>
              </div>

              {filteredAccounts.length > 1 && (
                <div>
                  <label className="label">{t('liveAutomation.config.socialAccount')}</label>
                  <select
                    value={formSocialAccountId}
                    onChange={(e) => setFormSocialAccountId(e.target.value)}
                    className="input appearance-none"
                  >
                    <option value="">{t('liveAutomation.config.selectAccount')}</option>
                    {filteredAccounts.map((acc) => (
                      <option key={acc.id} value={acc.id}>
                        @{acc.username} ({acc.platform})
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {filteredAccounts.length === 1 && (
                <div className="p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                  <p className="text-sm text-green-700 dark:text-green-300 flex items-center gap-2">
                    <Check className="w-4 h-4" />
                    {t('liveAutomation.config.socialAccount')}: <span className="font-medium">@{filteredAccounts[0].username}</span>
                  </p>
                </div>
              )}

              {filteredAccounts.length === 0 && (
                <div className="p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
                  <p className="text-sm text-yellow-700 dark:text-yellow-300">
                    {t('liveAutomation.config.noAccounts', 'No connected accounts for this platform. Connect one in Settings > Integrations.')}
                  </p>
                </div>
              )}

              <Input
                label={t('liveAutomation.config.sessionTitle')}
                value={formTitle}
                onChange={(e) => setFormTitle(e.target.value)}
                placeholder={t('liveAutomation.config.sessionTitlePlaceholder')}
                required
              />

              <Input
                label={t('liveAutomation.config.liveId')}
                value={formLiveId}
                onChange={(e) => setFormLiveId(e.target.value)}
                placeholder="123456789"
                helperText={t('liveAutomation.config.liveIdHint')}
              />
            </div>
          )}

          {/* Step 2: Marketplace Connection (optional) */}
          {activeTab === 1 && (
            <div className="space-y-4">
              <div className="p-4 bg-amber-50 dark:bg-amber-900/20 rounded-xl border border-amber-200 dark:border-amber-800">
                <div className="flex items-center gap-3">
                  <ShoppingBag className="w-5 h-5 text-amber-600" />
                  <div>
                    <h4 className="text-sm font-semibold text-gray-900 dark:text-white">
                      {t('liveAutomation.marketplace.title')} <span className="text-xs font-normal text-amber-600 dark:text-amber-400">({t('common.optional', 'optional')})</span>
                    </h4>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {t('liveAutomation.marketplace.connectDesc', 'Connect your store on marketplace.devskin.com to select products with photos directly. If you skip, you can enter product name and URL manually in the next step.')}
                    </p>
                  </div>
                </div>
              </div>

              <Input
                label={t('liveAutomation.marketplace.storeSlug')}
                value={formStoreSlug}
                onChange={(e) => setFormStoreSlug(e.target.value.toLowerCase().replace(/\s+/g, '-'))}
                placeholder="my-store"
                helperText={t('liveAutomation.marketplace.storeSlugHint2')}
              />

              {formStoreSlug && (
                <div className="p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                  <p className="text-sm text-green-700 dark:text-green-300 flex items-center gap-2">
                    <Check className="w-4 h-4" />
                    {t('liveAutomation.marketplace.connected', 'Connected to marketplace')}
                  </p>
                </div>
              )}

              {!formStoreSlug && (
                <div className="p-6 text-center border-2 border-dashed border-gray-200 dark:border-gray-700 rounded-xl">
                  <ShoppingBag className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                  <p className="text-sm text-gray-500">
                    {t('liveAutomation.marketplace.skipHint2', 'You can skip this step. Without marketplace connection, you will enter product name and URL manually for each keyword.')}
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Step 3: Keywords → Product → Workflow */}
          {activeTab === 2 && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="text-sm font-semibold text-gray-900 dark:text-white">{t('liveAutomation.keywords.title')}</h4>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                    {t('liveAutomation.keywords.keywordWorkflowDesc', 'Each keyword triggers a workflow when detected in live comments')}
                  </p>
                </div>
                <Button size="sm" variant="secondary" leftIcon={<Plus className="w-4 h-4" />} onClick={addRule}>
                  {t('liveAutomation.keywords.addRule')}
                </Button>
              </div>

              {/* Keyword Rules */}
              <div className="space-y-3 max-h-[400px] overflow-y-auto pr-1">
                {formKeywordRules.map((rule, index) => (
                  <div key={index} className="p-4 border border-gray-200 dark:border-gray-700 rounded-xl bg-gray-50/50 dark:bg-gray-800/30 space-y-3">
                    <div className="flex items-start justify-between">
                      <span className="text-xs font-semibold text-gray-500 dark:text-gray-400">#{index + 1}</span>
                      {formKeywordRules.length > 1 && (
                        <button onClick={() => removeRule(index)} className="p-1 rounded hover:bg-red-100 dark:hover:bg-red-900/20 text-red-400 hover:text-red-500">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>

                    {/* Keyword + Match Type */}
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      <div className="sm:col-span-2">
                        <Input
                          label={t('liveAutomation.keywords.keyword')}
                          placeholder={t('liveAutomation.keywords.keywordPlaceholder')}
                          value={rule.keyword}
                          onChange={(e) => updateRule(index, 'keyword', e.target.value)}
                        />
                      </div>
                      <div>
                        <label className="label">{t('liveAutomation.keywords.matchType', 'Match Type')}</label>
                        <select
                          value={rule.match_type || 'contains'}
                          onChange={(e) => updateRule(index, 'match_type', e.target.value)}
                          className="input appearance-none"
                        >
                          <option value="contains">{t('liveAutomation.keywords.contains', 'Contains')}</option>
                          <option value="exact">{t('liveAutomation.keywords.exact', 'Exact Match')}</option>
                        </select>
                      </div>
                    </div>

                    {/* Workflow */}
                    <div>
                      <label className="label">{t('liveAutomation.keywords.workflow', 'Workflow')} <span className="text-gray-400 font-normal text-xs">({t('common.optional', 'optional')})</span></label>
                      <select
                        value={rule.workflow_id || ''}
                        onChange={(e) => {
                          const wf = workflows.find((w: any) => w.id === e.target.value);
                          updateRule(index, 'workflow_id', e.target.value);
                          updateRule(index, 'workflow_name', wf?.name || '');
                        }}
                        className="input appearance-none"
                      >
                        <option value="">{t('liveAutomation.keywords.selectWorkflow', 'Select a workflow (optional)')}</option>
                        {workflows.map((wf: any) => (
                          <option key={wf.id} value={wf.id}>{wf.name}</option>
                        ))}
                      </select>
                      {rule.workflow_id && rule.workflow_name && (
                        <p className="text-xs text-green-600 dark:text-green-400 mt-1 flex items-center gap-1">
                          <Zap className="w-3 h-3" />
                          {t('liveAutomation.keywords.workflowLinked', 'Linked')}: {rule.workflow_name}
                        </p>
                      )}
                      {!rule.workflow_id && (
                        <p className="text-xs text-gray-400 mt-1">
                          {t('liveAutomation.keywords.noWorkflowHint', 'Without a workflow, the contact will be captured only (no automated action).')}
                        </p>
                      )}
                    </div>

                    {/* Row 2: Product + Coupon */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <label className="label">{t('liveAutomation.keywords.productName', 'Product')} <span className="text-gray-400 font-normal text-xs">({t('common.optional', 'optional')})</span></label>
                        {rule.product_name ? (
                          <div className="flex items-center gap-2 p-2.5 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                            {(rule as any).product_image && <img src={(rule as any).product_image} alt="" className="w-10 h-10 rounded-lg object-cover" />}
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-green-700 dark:text-green-300 truncate">{rule.product_name}</p>
                              {rule.product_url && <p className="text-xs text-green-600 dark:text-green-400 truncate">{rule.product_url}</p>}
                            </div>
                            <button type="button" onClick={() => { updateRule(index, 'product_name', ''); updateRule(index, 'product_url', ''); updateRule(index, 'product_image' as any, ''); }} className="p-1 hover:bg-green-100 dark:hover:bg-green-800 rounded">
                              <X className="w-3.5 h-3.5 text-green-600" />
                            </button>
                          </div>
                        ) : formStoreSlug ? (
                          <>
                            <button
                              type="button"
                              onClick={() => { setSelectingProductForRule(index); loadMarketplaceProducts(); }}
                              className="w-full p-2.5 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg text-sm text-gray-500 hover:border-red-400 hover:text-red-500 transition-colors flex items-center justify-center gap-2"
                            >
                              <ShoppingBag className="w-4 h-4" />
                              {t('liveAutomation.marketplace.selectProduct', 'Select Product')}
                            </button>
                            {/* Product picker */}
                            {selectingProductForRule === index && (
                              <div className="mt-2 border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden shadow-lg">
                                <div className="p-2.5 bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 flex items-center gap-2">
                                  <div className="relative flex-1">
                                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                                    <input type="text" placeholder={t('liveAutomation.marketplace.searchProducts', 'Search...')} value={productSearch} onChange={(e) => setProductSearch(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') loadMarketplaceProducts(productSearch); }} className="pl-9 pr-3 py-1.5 w-full rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-xs text-gray-900 dark:text-white" />
                                  </div>
                                  <button type="button" onClick={() => loadMarketplaceProducts(productSearch)} className="p-1.5 bg-red-500 text-white rounded-lg hover:bg-red-600"><Search className="w-3.5 h-3.5" /></button>
                                  <button type="button" onClick={() => setSelectingProductForRule(null)} className="p-1.5 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg"><X className="w-3.5 h-3.5 text-gray-500" /></button>
                                </div>
                                <div className="max-h-40 overflow-y-auto">
                                  {isLoadingProducts ? (
                                    <div className="p-4 text-center"><div className="animate-spin rounded-full h-5 w-5 border-b-2 border-red-500 mx-auto" /></div>
                                  ) : marketplaceProducts.length === 0 ? (
                                    <div className="p-4 text-center text-xs text-gray-500">{t('liveAutomation.marketplace.noProducts')}</div>
                                  ) : (
                                    marketplaceProducts.map((product: any) => (
                                      <button key={product.id} type="button" onClick={() => selectProductForRule(index, product)} className="w-full px-3 py-2.5 text-left hover:bg-red-50 dark:hover:bg-red-900/10 border-b border-gray-100 dark:border-gray-800 last:border-0 flex items-center gap-3 transition-colors">
                                        {product.images?.[0]?.url ? (
                                          <img src={product.images[0].url} alt="" className="w-10 h-10 rounded-lg object-cover" />
                                        ) : (
                                          <div className="w-10 h-10 rounded-lg bg-gray-100 dark:bg-gray-800 flex items-center justify-center"><ShoppingBag className="w-4 h-4 text-gray-400" /></div>
                                        )}
                                        <div className="flex-1 min-w-0">
                                          <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{product.name}</p>
                                          <p className="text-xs text-gray-500">{product.price ? `R$ ${Number(product.price).toFixed(2)}` : ''}</p>
                                        </div>
                                      </button>
                                    ))
                                  )}
                                </div>
                              </div>
                            )}
                          </>
                        ) : (
                          <div className="space-y-2">
                            <Input placeholder={t('liveAutomation.keywords.productNamePlaceholder', 'Product name')} value={rule.product_name || ''} onChange={(e) => updateRule(index, 'product_name', e.target.value)} />
                            <Input placeholder="https://..." value={rule.product_url || ''} onChange={(e) => updateRule(index, 'product_url', e.target.value)} />
                          </div>
                        )}
                      </div>
                      <Input
                        label={`${t('liveAutomation.keywords.couponCode', 'Coupon')} (${t('common.optional', 'optional')})`}
                        value={rule.coupon_code || ''}
                        onChange={(e) => updateRule(index, 'coupon_code', e.target.value)}
                        placeholder="LIVE10"
                      />
                    </div>
                  </div>
                ))}
              </div>

              <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                <p className="text-xs text-blue-600 dark:text-blue-400">
                  {t('liveAutomation.keywords.workflowHint', 'The product and coupon are sent as data ({{product.name}}, {{product.url}}, {{coupon}}) to the workflow. One workflow can serve all keywords — the product changes dynamically.')}
                </p>
              </div>
            </div>
          )}

          {/* Step 4: Settings */}
          {activeTab === 3 && (
            <div className="space-y-3 sm:space-y-6">
              <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800/50 rounded-xl">
                <div>
                  <h4 className="text-sm font-semibold text-gray-900 dark:text-white">{t('liveAutomation.settings.autoDetect')}</h4>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{t('liveAutomation.settings.autoDetectDesc')}</p>
                </div>
                <button type="button" onClick={() => setFormAutoDetect(!formAutoDetect)} className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ${formAutoDetect ? 'bg-red-500' : 'bg-gray-300 dark:bg-gray-600'}`}>
                  <span className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow transition duration-200 ${formAutoDetect ? 'translate-x-5' : 'translate-x-0'}`} />
                </button>
              </div>

              {/* Rate limiting */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="label">{t('liveAutomation.settings.maxPerMinute', 'Max responses/min')}</label>
                  <input
                    type="number"
                    min={1}
                    max={60}
                    value={formMaxPerMinute}
                    onChange={(e) => setFormMaxPerMinute(Math.max(1, Math.min(60, parseInt(e.target.value) || 30)))}
                    className="input"
                  />
                  <p className="text-xs text-gray-400 mt-1">{t('liveAutomation.settings.maxPerMinuteHint', 'Limit automated responses per minute (1-60). Meta allows ~200/hour.')}</p>
                </div>
                <div>
                  <label className="label">{t('liveAutomation.settings.responseDelay', 'Response delay (ms)')}</label>
                  <input
                    type="number"
                    min={0}
                    max={10000}
                    step={500}
                    value={formResponseDelay}
                    onChange={(e) => setFormResponseDelay(Math.max(0, Math.min(10000, parseInt(e.target.value) || 0)))}
                    className="input"
                  />
                  <p className="text-xs text-gray-400 mt-1">{t('liveAutomation.settings.responseDelayHint', 'Delay before responding (0-10000ms). Helps avoid looking like a bot.')}</p>
                </div>
              </div>

              <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-xl border border-blue-200 dark:border-blue-800">
                <div className="flex items-start gap-3">
                  <Zap className="w-5 h-5 text-blue-500 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-blue-800 dark:text-blue-300">
                      {t('liveAutomation.settings.workflowNote', 'How it works')}
                    </p>
                    <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                      {t('liveAutomation.settings.workflowNoteDesc', 'When a keyword is detected, the linked workflow is triggered. The workflow handles everything: DM response, product link, coupon, follow-up. Configure all of this in the Workflows page.')}
                    </p>
                    <a href="/workflows" className="inline-flex items-center gap-1 text-xs font-medium text-blue-700 dark:text-blue-300 mt-2 hover:underline">
                      <ArrowRight className="w-3.5 h-3.5" />
                      {t('liveAutomation.templates.goToWorkflows', 'Go to Workflows')}
                    </a>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Navigation buttons */}
          <div className="flex items-center justify-between pt-4 border-t border-gray-200 dark:border-gray-700">
            <div>
              {activeTab > 0 && (
                <Button variant="secondary" leftIcon={<ChevronLeft className="w-4 h-4" />} onClick={() => setActiveTab(activeTab - 1)}>
                  {t('common.back', 'Back')}
                </Button>
              )}
            </div>
            <div className="flex items-center gap-3">
              <Button variant="ghost" onClick={() => setShowModal(false)}>
                {t('common.cancel')}
              </Button>
              {activeTab < 3 ? (
                <Button
                  rightIcon={<ChevronRight className="w-4 h-4" />}
                  onClick={() => {
                    if (activeTab === 0 && !formTitle.trim()) {
                      toast.error(t('liveAutomation.config.sessionTitleRequired', 'Session title is required'));
                      return;
                    }
                    setActiveTab(activeTab + 1);
                  }}
                  className="bg-gradient-to-r from-red-500 to-pink-500 hover:from-red-600 hover:to-pink-600"
                >
                  {t('common.next', 'Next')}
                </Button>
              ) : (
                <Button
                  onClick={handleSaveSession}
                  isLoading={isSaving}
                  leftIcon={<Check className="w-4 h-4" />}
                  className="bg-gradient-to-r from-red-500 to-pink-500 hover:from-red-600 hover:to-pink-600"
                >
                  {editingSession ? t('common.save') : t('liveAutomation.createSession')}
                </Button>
              )}
            </div>
          </div>
        </div>
      </Modal>

      {/* Detect Lives Modal */}
      <Modal
        isOpen={showDetectModal}
        onClose={() => setShowDetectModal(false)}
        title={t('liveAutomation.detectLives')}
        size="md"
      >
        {isDetecting ? (
          <div className="flex flex-col items-center justify-center py-12">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-red-500 mb-4" />
            <p className="text-sm text-gray-500">{t('common.loading')}</p>
          </div>
        ) : detectedLives.length === 0 ? (
          <div className="text-center py-12">
            <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-gray-100 to-gray-50 dark:from-gray-800 dark:to-gray-900 flex items-center justify-center">
              <WifiOff className="w-8 h-8 text-gray-400" />
            </div>
            <p className="text-gray-900 dark:text-white font-semibold">{t('liveAutomation.monitoring.noLivesDetected')}</p>
          </div>
        ) : (
          <div className="space-y-3">
            {detectedLives.map((live, i) => (
              <div
                key={i}
                className="p-4 border border-gray-200 dark:border-gray-700 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-800/50 cursor-pointer transition-colors"
                onClick={() => {
                  setShowDetectModal(false);
                  setEditingSession(null);
                  resetForm();
                  setFormPlatform(live.platform);
                  setFormLiveId(live.live_id);
                  setFormTitle(live.title);
                  if ((live as any).social_account_id) {
                    setFormSocialAccountId((live as any).social_account_id);
                  }
                  setShowModal(true);
                }}
              >
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${PLATFORM_CONFIG[live.platform]?.bgColor || 'bg-gray-100'} ${PLATFORM_CONFIG[live.platform]?.darkBgColor || 'dark:bg-gray-800'}`}>
                    <Radio className={`w-5 h-5 ${PLATFORM_CONFIG[live.platform]?.color || 'text-gray-500'}`} />
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-gray-900 dark:text-white">{live.title}</p>
                    <p className="text-xs text-gray-500">
                      {live.account_name} {live.viewer_count ? `- ${live.viewer_count} viewers` : ''}
                    </p>
                  </div>
                  <span className="relative flex h-3 w-3">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-3 w-3 bg-red-500"></span>
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default LiveAutomationPage;
